<template>
	<div class="parcel-card">
		<div class="top-section" ref="headerEl" data-tour="parcel-overview">
			<div class="header">
				<div class="header-top">
					<div class="title-group">
						<h3 class="title">{{ $t("parcelCard.lot") }} {{ item.parcel_id }}</h3>
						<CopyButton :text="String(item.parcel_id)" :title="$t('callToActions.copyLotNumber')" />
						<LinksIcons v-if="mapsLinks.length > 0" class="maps-link" :links="mapsLinks" />
					</div>
					<div class="header-actions">
						<button
							v-if="!compact"
							type="button"
							class="icon-button collapse-btn"
							:title="$t('callToActions.collapsePanel')"
							:aria-label="$t('callToActions.collapsePanel')"
							@click="emit('collapse')"
						>
							<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
						</button>
						<button
							type="button"
							class="icon-button collapse-btn"
							:title="$t('callToActions.close')"
							:aria-label="$t('callToActions.close')"
							@click="emit('close')"
						>
							<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
						</button>
					</div>
				</div>

				<div v-if="addressLine" class="sub-title">{{ addressLine }}</div>

				<div
					v-if="store.isUserLoggedIn && (planningBadges.length > 0 || showZoneBadge || constraintBadges.length > 0)"
					class="badge-groups"
				>
					<div v-if="planningBadges.length > 0 || showZoneBadge" class="badge-group">
						<span class="group-label">{{ $t("parcelCard.badges.planningGroup") }}</span>
						<div class="badge-row">
							<span
								v-for="badge in planningBadges"
								:key="badge.key"
								class="badge badge--urbanism"
								:style="
									badge.color
										? { backgroundColor: `color-mix(in srgb, ${badge.color} 10%, transparent)`, color: badge.color }
										: undefined
								"
							>
								{{ badge.label }}
							</span>
							<span v-if="showZoneBadge" class="badge badge--zone">
								{{ $t("parcelCard.zone") }} {{ item.zone_code }}
							</span>
						</div>
					</div>
					<div v-if="constraintBadges.length > 0" class="badge-group">
						<span class="group-label">{{ $t("parcelCard.badges.constraintGroup") }}</span>
						<div class="badge-row">
							<span v-for="badge in constraintBadges" :key="badge.key" class="badge badge--constraint">
								{{ badge.label }}
							</span>
						</div>
					</div>
				</div>
			</div>

			<SegmentedToggle v-if="isAssistantAvailable" v-model="activeView" class="view-switch" :options="viewOptions" />

			<div class="separator"></div>
		</div>

		<RegulatoryAssistantPanel
			v-if="activeView === ParcelCardView.Assistant"
			:parcel-id="String(item.parcel_id)"
			:zone-code="item.zone_code"
		/>

		<div v-else class="scroll-body">
			<div v-if="item.official_area_m2" class="stat-tiles" data-tour-related="parcel-overview">
				<div class="tile">
					<span class="label">{{ $t("parcelCard.cadastralArea") }}</span>
					<div class="value-group">
						<span class="value">{{ formatArea(item.official_area_m2) }}</span>
						<CopyButton :text="formatArea(item.official_area_m2)" :title="$t('callToActions.copyArea')" />
					</div>
				</div>
			</div>

			<div v-if="hasActions" class="actions-box" data-tour-related="parcel-overview">
				<span class="label">{{ $t("parcelCard.accordions.actions") }}</span>
				<div class="actions-content">
					<div class="parcel-actions">
						<div v-if="showCreateTargetButton" class="action-slot">
							<Button
								type="button"
								variant="target"
								size="m"
								style="width: 100%; height: 100%; min-width: 0"
								:disabled="isQuickOpportunityLoading"
								@click="handleQuickOpportunity"
							>
								<span v-if="isQuickOpportunityLoading" class="loader"></span>
								<span v-else>{{ $t("callToActions.createOpportunity") }}</span>
							</Button>
						</div>

						<div v-if="canExportParcel && !canExportParcelFiles" class="action-slot">
							<Button
								type="button"
								variant="primary"
								size="m"
								style="width: 100%; height: 100%; min-width: 0"
								:disabled="isExporting"
								@click="exportParcelReport"
							>
								<span v-if="isExporting" class="loader"></span>
								<template v-else>
									<AppIcon :name="IconName.Download" class="export-icon" :size="16" :stroke-width="1.75" />
									{{ $t("callToActions.downloadPdf") }}
								</template>
							</Button>
						</div>
						<div v-else-if="canExportParcel" class="action-slot">
							<Menu same-width :gutter="2" @select="onExportMenuSelect">
								<template #trigger>
									<Button
										type="button"
										variant="primary"
										size="m"
										style="width: 100%; height: 100%; min-width: 0"
										:disabled="isExporting"
									>
										<span v-if="isExporting" class="loader"></span>
										<template v-else>
											<AppIcon :name="IconName.Download" class="export-icon" :size="16" :stroke-width="1.75" />
											{{ $t("callToActions.exportParcel") }}
											<AppIcon :name="IconName.ChevronDown" class="menu-trigger-chevron" :size="10" :stroke-width="2" />
										</template>
									</Button>
								</template>
								<MenuItem value="pdf" class="export-menu-item">
									<span class="format">{{ $t("callToActions.downloadPdf") }}</span>
									<span class="description">{{ $t("callToActions.pdfExportDescription") }}</span>
								</MenuItem>
								<MenuItem v-if="canExportParcelFiles" value="shapefile" class="export-menu-item">
									<span class="format">{{ $t("callToActions.downloadShapefile") }}</span>
									<span class="description">{{ $t("callToActions.shapefileExportDescription") }}</span>
								</MenuItem>
								<MenuItem v-if="canExportParcelFiles" value="kml" class="export-menu-item">
									<span class="format">KML</span>
									<span class="description">{{ $t("callToActions.kmlExportDescription") }}</span>
								</MenuItem>
							</Menu>
						</div>
					</div>

					<section v-if="hasTransactionSection" ref="transactionSectionEl" class="transaction-section">
						<form
							class="transaction-form"
							:class="{ expanded: showTransactionForm }"
							@submit.prevent="createTransactionEnabled && handleCreateTransaction()"
						>
							<div class="input-wrapper">
								<input
									ref="transactionInputEl"
									type="text"
									class="input-transaction"
									name="inscriptionNumber"
									v-model="inscriptionNumber"
									:placeholder="$t('transaction.inscription')"
									pattern="\s*\d{2}\s*\d{3}\s*\d{3}\s*"
									:disabled="!showTransactionForm"
									required
									@input="
										event => (createTransactionEnabled = (event.currentTarget as HTMLInputElement)?.validity.valid)
									"
								/>
							</div>
							<Button
								type="button"
								variant="tertiary"
								size="m"
								class="create-transaction-btn"
								:disabled="showTransactionForm && !createTransactionEnabled"
								@click="handleTransactionButtonClick"
							>
								<span class="label-full">{{ $t("callToActions.createTransaction") }}</span>
								<span class="label-short">{{ $t("callToActions.createTransactionShort") }}</span>
							</Button>
						</form>
					</section>
					<p v-if="isExtendedAccessActive" class="access-note">
						{{ $t("extendedAccess.parcelReportNote", { days: extendedAccessStore.daysLeft }) }}
					</p>
				</div>
			</div>

			<div v-if="showExtendedAccessCta" class="cta-box">
				<Button type="button" variant="primary" size="m" class="cta-button" @click="isExtendedAccessModalOpen = true">
					<AppIcon :name="IconName.FileText" :size="16" :stroke-width="1.75" />
					{{ $t("extendedAccess.cta") }}
				</Button>
				<ExtendedAccessModal v-model:open="isExtendedAccessModalOpen" />
			</div>

			<div class="separator"></div>

			<div class="accordions" ref="itemsEl" data-tour="parcel-details">
				<AccordionRoot :multiple="true" :default-value="[]" persistence-key="parcel-card-accordion">
					<AccordionItem v-if="visibleRows(lotRows).length > 0" value="lot" :title="$t('parcelCard.accordions.lot')">
						<div class="content">
							<div v-for="detailRow in visibleRows(lotRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem v-if="canReadZoning" value="zoning" :title="$t('zoning.title')">
						<div class="zoning">
							<div v-if="zoningDetails.length === 0" class="zone">
								<div class="content">
									<div class="detail">
										<span class="label">{{ $t("zoning.zone") }}</span>
										<span class="value">{{ item.zone_code }}</span>
									</div>
								</div>
								<div v-if="isZoningLoading" class="skeleton-rows" aria-busy="true">
									<div v-for="index in 6" :key="index" class="skeleton-row">
										<span class="skeleton label"></span>
										<span class="skeleton value"></span>
									</div>
								</div>
							</div>
							<div v-for="zone in zoningDetails" :key="zone.zoneCode" class="zone">
								<div class="content">
									<div v-for="detailRow in zone.rows" :key="detailRow.label" class="detail">
										<span class="label">{{ detailRow.label }}</span>
										<ExpandableText class="value" :text="detailRow.value" />
									</div>
									<div v-if="zone.gridUrl" class="detail">
										<span class="label">{{ $t("zoning.grid") }}</span>
										<span class="value">
											<LinkPill :href="zone.gridUrl">{{ $t("zoning.open") }}</LinkPill>
										</span>
									</div>
								</div>
								<AccordionRoot
									v-if="zone.blocks.length > 0"
									:persistent="false"
									:default-value="[`norms-${zone.zoneCode}`]"
								>
									<AccordionItem
										class="norms"
										:value="`norms-${zone.zoneCode}`"
										:title="$t('zoning.norms')"
										:active-count="zone.blocks.length"
										ghost
									>
										<AccordionRoot class="blocks" :multiple="true" :persistent="false">
											<template v-for="(block, index) in zone.blocks" :key="index">
												<AccordionItem
													v-if="block.rows.length > 0"
													class="block"
													:value="`block-${index}`"
													:title="block.title"
													ghost
												>
													<div class="content">
														<div v-for="detailRow in block.rows" :key="detailRow.label" class="detail">
															<span class="label">{{ detailRow.label }}</span>
															<ExpandableText class="value" :text="detailRow.value" />
														</div>
													</div>
												</AccordionItem>
												<span v-else class="block title">{{ block.title }}</span>
											</template>
										</AccordionRoot>
									</AccordionItem>
								</AccordionRoot>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="
							store.currentUser?.has(Permission.DevPotentialRead) || visibleRows(developmentPotentialRows).length > 0
						"
						value="developmentPotential"
						:title="$t('parcelCard.accordions.developmentPotential')"
					>
						<div class="content">
							<div v-if="store.currentUser?.has(Permission.DevPotentialWrite)" class="dev-potential-section">
								<label for="dev-potential-single">{{ $t("development-potential.title") }}</label>
								<ArkListboxDropdown
									id="dev-potential-single"
									v-model="devPotentialCode"
									:options="devPotentialOptions"
									:clearable="false"
									:multiple="false"
									style="flex: 0 0 var(--width-small-input); width: var(--width-small-input)"
								/>
							</div>
							<div v-else-if="store.currentUser?.has(Permission.DevPotentialRead)" class="detail">
								<span class="label">{{ $t("development-potential.title") }}</span>
								<span class="value">{{ devPotentialLabel }}</span>
							</div>
							<div v-for="detailRow in visibleRows(developmentPotentialRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="visibleRows(propertyRows).length > 0"
						value="property"
						:title="$t('parcelCard.accordions.property')"
					>
						<div class="content">
							<div v-for="detailRow in visibleRows(propertyRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="visibleRows(buildingRows).length > 0"
						value="building"
						:title="$t('parcelCard.accordions.building')"
					>
						<div class="content">
							<div v-for="detailRow in visibleRows(buildingRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="visibleRows(valueRows).length > 0"
						value="value"
						:title="$t('parcelCard.accordions.value')"
					>
						<div class="content">
							<div v-for="detailRow in visibleRows(valueRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="store.isUserLoggedIn && visibleRows(environmentRows).length > 0"
						value="environment"
						:title="$t('parcelCard.accordions.environment')"
					>
						<div class="content">
							<div v-for="detailRow in visibleRows(environmentRows)" :key="detailRow.label" class="detail">
								<span class="label">{{ detailRow.label }}</span>
								<span class="value">{{ detailRow.value }}</span>
							</div>
						</div>
					</AccordionItem>
				</AccordionRoot>

				<LoginCta
					v-if="!store.isUserLoggedIn"
					:title="$t('parcelCard.loginCta.title')"
					:message="$t('parcelCard.loginCta.message')"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch, watchEffect } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { formatInscriptionNumber, formatQuantity } from "@/utils/format"
import { onClickOutside, useElementSize } from "@vueuse/core"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import CopyButton from "@/components/ui/CopyButton.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import Button from "@/components/ui/Button.vue"
import ExpandableText from "@/components/ui/ExpandableText.vue"
import LinkPill from "@/components/ui/LinkPill.vue"
import Menu from "@/components/ui/Menu.vue"
import MenuItem from "@/components/ui/MenuItem.vue"
import ExtendedAccessModal from "@/components/modals/ExtendedAccessModal.vue"
import LoginCta from "@/components/ui/LoginCta.vue"
import LinksIcons, { type EntityLink } from "@/components/ui/LinksIcons.vue"
import SegmentedToggle from "@/components/ui/SegmentedToggle.vue"
import RegulatoryAssistantPanel from "@/components/parcels/assistant/RegulatoryAssistantPanel.vue"
import { UseRootStore, Permission } from "@/model/RootStore.ts"
import { DevelopmentPotential } from "@/model/DataModel.ts"
import { useProspectStore } from "@/stores/prospectStore.ts"
import { useOpportunityStore } from "@/stores/opportunityStore"
import { useToastStore } from "@/stores/toastStore.ts"
import { useExtendedAccessStore } from "@/stores/extendedAccessStore"
import { ProjectStoreKey, isHuntingMode } from "@/stores/projectStore.ts"
import { config, Feature } from "@/AppConfig"
import { setProspectPotential, getParcel, createOpportunity, type ParcelExportFormat } from "@/utils/ApiClient"
import { formatDateToISO } from "@/utils/date"
import * as download from "@/utils/download"
import { create as createParcelPdfV2, type ParcelReportV2Content } from "@/utils/parcelPdfV2"
import { ParcelPdfV2Metric, parcelPdfV2Profile } from "@/utils/parcelPdfV2Profile"
import { zoneDetails } from "@/utils/parcelZoning"
import { captureParcelMap, ParcelMapView } from "@/utils/parcelMapSnapshot"
import landerzLogo from "@/assets/images/menu-left/landerz-logo.png?inline"
import type { ParcelReportContent } from "@/types/parcelReport"
import { REGULATORY_ASSISTANT_MUNICIPALITY_CODE } from "@/types/regulatoryAssistant"
import { ProjectType, type Zone } from "@/graphql"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { FieldAccess, canAccessField, type FieldVisibility } from "@/utils/fieldAccess"
import { AreaUnit, DistanceUnit } from "@/utils/unitSystem"

const props = defineProps<{
	item: Record<string, any>
	mapRef?: any
	canCreateQuickOpportunity?: boolean
	compact?: boolean
}>()
const emit = defineEmits<{
	(e: "collapse"): void
	(e: "close"): void
	(e: "header-resize", height: number): void
	(e: "assistant-view", isOpen: boolean): void
}>()

// reports the header's height (incl. top padding/separator) so the mobile drawer can peek to exactly it
const headerEl = ref<HTMLElement | null>(null)
const itemsEl = ref<HTMLElement | null>(null)
const { height: headerHeight } = useElementSize(headerEl)
watch(
	headerHeight,
	() => {
		const peekHeight = itemsEl.value?.offsetTop ?? headerHeight.value
		if (peekHeight > 0) emit("header-resize", peekHeight)
	},
	{ immediate: true }
)

const { t, locale } = useI18n()
const { areaUnitSymbol, convertAreaToPreferredUnit, convertDistanceToPreferredUnit, distanceUnitSymbol } =
	useUnitSystem()
const store = UseRootStore()
const opportunityStore = useOpportunityStore()
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const isHunting = computed(() => isHuntingMode())

// gates everything beyond lot number, address, and cadastral area for CCA clients and Landerz staff
const hasFullAccess = computed(() => store.currentUser?.has(Permission.ParcelSensitiveRead) ?? false)

enum ParcelCardView {
	Details = "details",
	Assistant = "assistant",
}

const isAssistantAvailable = computed(
	() =>
		config.ui.has(Feature.Assistant) &&
		(store.currentUser?.has(Permission.RegulatoryAssistantUse) ?? false) &&
		String(props.item.mun_code) === REGULATORY_ASSISTANT_MUNICIPALITY_CODE
)

const activeView = ref(ParcelCardView.Details)

const viewOptions = computed(() => [
	{ value: ParcelCardView.Details, label: t("parcelCard.views.details"), icon: IconName.FileLines },
	{ value: ParcelCardView.Assistant, label: t("parcelCard.views.assistant"), icon: IconName.Sparkles },
])

watch(
	() => props.item?.parcel_id,
	() => {
		activeView.value = ParcelCardView.Details
	}
)

watch(activeView, view => emit("assistant-view", view === ParcelCardView.Assistant))

const hasTransactionSection = computed(
	() => store.currentUser?.has(Permission.TransactionWrite) && currentProject.value?.type !== ProjectType.Cca
)

// only shown below the cadastral area while hunting
const showCreateTargetButton = computed(
	() => props.canCreateQuickOpportunity && !!currentProject.value && isHunting.value
)

const canExportParcelFiles = computed(() => store.isUserLoggedIn && store.currentUser.has(Permission.ParcelExport))
const canExportParcel = computed(
	() =>
		canExportParcelFiles.value ||
		(config.ui.has(Feature.ExtendedAccess) &&
			store.isUserLoggedIn &&
			store.currentUser.has(Permission.ParcelReportExport))
)

const extendedAccessStore = useExtendedAccessStore()
const { isActive: isExtendedAccessActive } = storeToRefs(extendedAccessStore)
const isExtendedAccessModalOpen = ref(false)
watch(
	() => store.userApiToken,
	() => {
		if (config.ui.has(Feature.ExtendedAccess)) extendedAccessStore.load()
	},
	{ immediate: true }
)

const showExtendedAccessCta = computed(
	() => config.ui.has(Feature.ExtendedAccess) && !canExportParcel.value && extendedAccessStore.canStartExtendedAccess
)

const hasActions = computed(
	() =>
		(hasFullAccess.value && (showCreateTargetButton.value || canExportParcel.value || hasTransactionSection.value)) ||
		(canExportParcel.value && isExtendedAccessActive.value)
)

function isMissing(value: unknown): boolean {
	return value === null || value === undefined || value === "" || value === "null"
}

function formatText(value: unknown): string {
	if (isMissing(value)) return t("opportunities.unknown")
	return Array.isArray(value) ? value.join(", ") : String(value)
}

function formatInt(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	return formatQuantity(numberValue)
}

function formatArea(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	const convertedArea = convertAreaToPreferredUnit(numberValue, AreaUnit.SquareMeters)
	return `${formatQuantity(Math.round(convertedArea))} ${areaUnitSymbol.value}`
}

function formatLinear(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	const convertedDistance = convertDistanceToPreferredUnit(numberValue, DistanceUnit.Meters)
	return `${formatQuantity(convertedDistance, { maximumFractionDigits: 1 })} ${distanceUnitSymbol.value}`
}

function formatCurrency(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	return `${formatQuantity(Math.round(numberValue))}$`
}

function formatPercent(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	return `${formatQuantity(numberValue, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

function formatYesNo(value: unknown): string {
	if (isMissing(value)) return t("opportunities.unknown")
	return value === true || value === "true" ? t("parcelCard.yes") : t("parcelCard.no")
}

function formatDate(value: unknown): string {
	if (isMissing(value)) return t("opportunities.unknown")
	const dateValue = value instanceof Date ? value : new Date(value as string)
	return Number.isNaN(dateValue.getTime()) ? t("opportunities.unknown") : formatDateToISO(dateValue)
}

function formatYear(value: unknown): string {
	const numberValue = Number(value)
	if (isMissing(value) || !Number.isFinite(numberValue)) return t("opportunities.unknown")
	return String(Math.round(numberValue))
}

function formatOwnerType(value: unknown): string {
	if (isMissing(value)) return t("opportunities.unknown")
	switch (String(value)) {
		case "1":
			return t("parcelCard.ownerTypeIndividual")
		case "2":
			return t("parcelCard.ownerTypeCorporate")
		default:
			return String(value)
	}
}

function formatAgriculturalZoning(value: unknown): string {
	if (isMissing(value)) return t("opportunities.unknown")
	switch (String(value)) {
		case "0":
			return t("parcelCard.agriculturalZoningExcluded")
		case "1":
			return t("parcelCard.agriculturalZoningPartial")
		case "2":
			return t("parcelCard.agriculturalZoningFull")
		default:
			return String(value)
	}
}

interface DetailRow {
	label: string
	value: string
	visibility: FieldVisibility
}

function isFieldVisible(visibility: FieldVisibility): boolean {
	return canAccessField(store.currentUser, visibility)
}

function visibleRows(rows: DetailRow[]): DetailRow[] {
	return rows.filter(detailRow => isFieldVisible(detailRow.visibility))
}

const unknownLabel = computed(() => t("opportunities.unknown"))

// omits the row entirely instead of showing an "unknown" placeholder
function row(
	label: string,
	raw: unknown,
	formatter: (value: unknown) => string,
	visibility: FieldVisibility = FieldAccess.Sensitive
): DetailRow | null {
	const value = formatter(raw)
	return value === unknownLabel.value ? null : { label, value, visibility }
}

function buildRows(entries: Array<DetailRow | null>): DetailRow[] {
	return entries.filter((entry): entry is DetailRow => entry !== null)
}

const addressLine = computed(() => [props.item.parcel_address, props.item.mun_name].filter(Boolean).join(", "))

const urbanismFlags = computed(() => [
	{
		key: "policy_tod_area",
		name: "name_tod",
		label: t("parcelCard.badges.tod"),
		color: "var(--accent-planning-policy-tod)",
		visibility: Permission.PlanningPolicyRead,
	},
	{
		key: "policy_ppu_area",
		name: "name_ppu",
		label: t("parcelCard.badges.ppu"),
		color: "var(--accent-planning-policy-ppu)",
		visibility: Permission.PlanningPolicyRead,
	},
	{
		key: "policy_corridor_area",
		name: "name_corridor",
		label: t("parcelCard.badges.corridor"),
		color: "var(--accent-planning-policy-corridor)",
		visibility: Permission.PlanningPolicyRead,
	},
])

// SPI and Planification designations are shown as a single "Secteur stratégique" chip/row
const strategicSectorActive = computed(
	() => props.item.policy_spi_area === true || props.item.policy_planif_area === true
)
const strategicSectorName = computed(() => props.item.name_spi ?? props.item.name_planif)

const constraintFlags = computed(() => [
	{
		key: "env_rainette_bool",
		label: t("parcelCard.badges.rainette"),
		color: undefined as string | undefined,
		visibility: Permission.EndangeredSpeciesRead as FieldVisibility,
	},
	{
		key: "env_floodplain_bool",
		label: t("parcelCard.badges.floodplain"),
		color: undefined as string | undefined,
		visibility: Permission.FloodableZonesRead as FieldVisibility,
	},
	{
		key: "env_wetland_bool",
		label: t("parcelCard.badges.wetland"),
		color: undefined as string | undefined,
		visibility: Permission.WetlandsRead as FieldVisibility,
	},
	{
		key: "env_soil_contaminated_bool",
		label: t("parcelCard.badges.soilContaminated"),
		color: undefined as string | undefined,
		visibility: FieldAccess.Public as FieldVisibility,
	},
])

const planningBadges = computed(() => [
	...urbanismFlags.value.filter(flag => props.item[flag.key] === true && isFieldVisible(flag.visibility)),
	...(strategicSectorActive.value && isFieldVisible(Permission.PlanningPolicyRead)
		? [
				{
					key: "policy_strategic_sector",
					label: t("parcelCard.badges.strategicSector"),
					color: "var(--accent-planning-policy-strategic)",
				},
			]
		: []),
])

const constraintBadges = computed(() =>
	constraintFlags.value.filter(flag => props.item[flag.key] === true && isFieldVisible(flag.visibility))
)

const showZoneBadge = computed(() => Boolean(props.item.zone_code) && isFieldVisible(Permission.ZoningRead))
const canReadZoning = computed(
	() => Boolean(props.item.zone_code) && Boolean(store.currentUser?.has(Permission.ZoningRead))
)

const valueVariation = computed(() => {
	if (isMissing(props.item.total_property_value) || isMissing(props.item.previous_total_property_value)) return null
	const total = Number(props.item.total_property_value)
	const previous = Number(props.item.previous_total_property_value)
	if (!Number.isFinite(total) || !Number.isFinite(previous) || previous === 0) return null
	const absolute = total - previous
	return { absolute, relative: (absolute / previous) * 100 }
})

const lotRows = computed(() =>
	buildRows([
		row(t("parcelCard.address"), props.item.parcel_address, formatText, FieldAccess.Public),
		row(t("parcelCard.municipality"), props.item.mun_name, formatText, FieldAccess.Public),
		row(t("parcelCard.province"), props.item.province, formatText, FieldAccess.Public),
	])
)

const developmentPotentialRows = computed(() =>
	buildRows([
		...urbanismFlags.value
			.filter(flag => props.item[flag.key] === true)
			.map(flag => row(flag.label, props.item[flag.name], formatText, flag.visibility)),
		strategicSectorActive.value
			? row(
					t("parcelCard.badges.strategicSector"),
					strategicSectorName.value,
					formatText,
					Permission.PlanningPolicyRead
				)
			: null,
		row(t("parcelCard.servicedVacantLand"), props.item.serviced_vacant_land, formatYesNo),
	])
)

const propertyRows = computed(() =>
	buildRows([
		row(t("parcelCard.cadastralArea"), props.item.official_area_m2, formatArea),
		row(t("parcelCard.frontage"), props.item.frontage_m, formatLinear),
		row(t("parcelCard.propertyUse"), props.item.property_use_label, formatText),
		row(t("parcelCard.ownerType"), props.item.owner_type, formatOwnerType),
		row(t("parcelCard.unitCount"), props.item.unit_count, formatInt),
		row(t("parcelCard.rollInscriptions"), props.item.owner_count, formatInt),
		row(t("parcelCard.dwellingUnits"), props.item.number_of_dwelling_units, formatInt),
		row(t("parcelCard.nonResidentialUnits"), props.item.number_of_non_residential_units, formatInt),
		row(t("parcelCard.rentalRooms"), props.item.number_of_rental_rooms, formatInt),
		row(t("parcelCard.nonResidentialClass"), props.item.non_residential_property_class, formatText),
		row(t("parcelCard.industrialClass"), props.item.industrial_class, formatText),
	])
)

const buildingRows = computed(() =>
	buildRows([
		row(t("parcelCard.constructionType"), props.item.construction_type_label, formatText),
		row(t("parcelCard.physicalLink"), props.item.physical_link_label, formatText),
		row(t("parcelCard.storeys"), props.item.number_of_storeys, formatInt),
		row(t("parcelCard.floorArea"), props.item.floor_area_m2, formatArea),
		row(t("parcelCard.constructionYear"), props.item.construction_year, formatYear),
	])
)

const valueRows = computed(() => {
	const rows = buildRows([
		row(t("parcelCard.assessmentRollYear"), props.item.assessment_roll_year, formatYear),
		row(t("parcelCard.marketReferenceDate"), props.item.market_reference_date, formatDate),
		row(t("parcelCard.landValue"), props.item.land_value, formatCurrency),
		row(t("parcelCard.buildingValue"), props.item.building_value, formatCurrency),
		row(t("parcelCard.totalValue"), props.item.total_property_value, formatCurrency),
		row(t("parcelCard.previousValue"), props.item.previous_total_property_value, formatCurrency),
	])
	if (valueVariation.value) {
		rows.push(
			{
				label: t("parcelCard.variationAbsolute"),
				value: formatCurrency(valueVariation.value.absolute),
				visibility: FieldAccess.Sensitive,
			},
			{
				label: t("parcelCard.variationRelative"),
				value: formatPercent(valueVariation.value.relative),
				visibility: FieldAccess.Sensitive,
			}
		)
	}
	return rows
})

const environmentRows = computed(() => {
	const rows = buildRows([
		row(
			t("parcelCard.agriculturalZoning"),
			props.item.agricultural_zoning,
			formatAgriculturalZoning,
			FieldAccess.Public
		),
		row(t("parcelCard.soilCapability"), props.item.env_soil_capability, formatText, Permission.SoilCapabilityRead),
		row(t("parcelCard.agriculturalZoneArea"), props.item.eae_agricultural_zone_area_m2, formatArea, FieldAccess.Public),
		row(t("parcelCard.forestZoneArea"), props.item.svfe_agricultural_zone_area_m2, formatArea, FieldAccess.Public),
		row(t("parcelCard.maxTaxableArea"), props.item.eae_max_taxable_area_m2, formatArea, FieldAccess.Public),
		row(t("parcelCard.floodplainZone"), props.item.env_floodplain_bool, formatYesNo, Permission.FloodableZonesRead),
		row(t("parcelCard.wetland"), props.item.env_wetland_bool, formatYesNo, Permission.WetlandsRead),
	])
	if (props.item.env_wetland_bool) {
		rows.push(
			...buildRows([
				row(t("parcelCard.wetlandShare"), props.item.env_wetland_per, formatPercent, Permission.WetlandsRead),
				row(t("parcelCard.wetlandArea"), props.item.env_wetland_area, formatArea, Permission.WetlandsRead),
			])
		)
	}
	rows.push(
		...buildRows([
			row(t("parcelCard.rainette"), props.item.env_rainette_bool, formatYesNo, Permission.EndangeredSpeciesRead),
			row(t("parcelCard.soilContaminated"), props.item.env_soil_contaminated_bool, formatYesNo, FieldAccess.Public),
		])
	)
	if (props.item.env_soil_contaminated_bool) {
		const rehab = row(
			t("parcelCard.soilRehabStatus"),
			props.item.env_soil_contaminated_rehab,
			formatText,
			FieldAccess.Public
		)
		if (rehab) rows.push(rehab)
	}
	rows.push(
		...buildRows([
			row(
				t("parcelCard.protectedAnimals"),
				props.item.env_protected_animals,
				formatText,
				Permission.EndangeredSpeciesRead
			),
			row(
				t("parcelCard.protectedPlants"),
				props.item.env_protected_plants,
				formatText,
				Permission.EndangeredSpeciesRead
			),
		])
	)
	return rows
})

// Development potential

const prospectStore = useProspectStore()
const toastStore = useToastStore()

watch(
	() => props.item.parcel_id,
	async parcelId => {
		if (!parcelId || !store.currentUser?.has(Permission.DevPotentialRead)) return
		const id = parcelId.toString()
		const existing = prospectStore.prospects.find(p => p.prospectID === id)
		if (!existing) {
			await prospectStore.loadProspect(store.userApiToken, id)
		}
	},
	{ immediate: true }
)

const devPotentialOptions = computed(() => [
	{ value: DevelopmentPotential.High, label: t("development-potential.high") },
	{ value: DevelopmentPotential.Medium, label: t("development-potential.medium") },
	{ value: DevelopmentPotential.Low, label: t("development-potential.low") },
	{ value: DevelopmentPotential.None, label: t("development-potential.none") },
	{ value: DevelopmentPotential.Unspecified, label: t("development-potential.unspecified") },
])

const devPotentialLabel = computed(
	() =>
		devPotentialOptions.value.find(option => option.value === devPotentialCode.value)?.label ??
		t("opportunities.unknown")
)

const devPotentialCode = computed<DevelopmentPotential | null>({
	get() {
		const parcelId = props.item.parcel_id?.toString()
		if (!parcelId) return null
		return prospectStore.prospects.find(p => p.prospectID === parcelId)?.potential ?? DevelopmentPotential.Unspecified
	},
	set(potential) {
		if (potential) {
			updateDevPotential(potential)
		}
	},
})

const parcelContent = computed<ParcelReportContent>(() => ({
	title: `${t("parcelCard.lot")} ${props.item.parcel_id}`,
	subtitle: addressLine.value,
	sections: [
		{ title: t("parcelCard.accordions.lot"), rows: lotRows.value },
		{
			title: t("parcelCard.accordions.developmentPotential"),
			rows: [
				...(store.currentUser?.has(Permission.DevPotentialRead)
					? [{ label: t("development-potential.title"), value: devPotentialLabel.value }]
					: []),
				...developmentPotentialRows.value,
			],
		},
		{ title: t("parcelCard.accordions.property"), rows: propertyRows.value },
		{ title: t("parcelCard.accordions.building"), rows: buildingRows.value },
		{ title: t("parcelCard.accordions.value"), rows: valueRows.value },
		{ title: t("parcelCard.accordions.environment"), rows: environmentRows.value },
	],
}))

function buildParcelReportV2Content(): ParcelReportV2Content {
	const profile = parcelPdfV2Profile(props.item.asset_type, props.item.asset_category)
	const currency = (value: unknown) => {
		const numberValue = Number(value)
		return isMissing(value) || !Number.isFinite(numberValue)
			? unknownLabel.value
			: `${numberValue < 0 ? "-" : ""}$${formatQuantity(Math.abs(Math.round(numberValue)))}`
	}
	const metricArea = (value: unknown) => {
		const numberValue = Number(value)
		return isMissing(value) || !Number.isFinite(numberValue)
			? unknownLabel.value
			: `${formatQuantity(Math.round(numberValue))} m²`
	}
	const metricFrontage = (value: unknown) => {
		const numberValue = Number(value)
		return isMissing(value) || !Number.isFinite(numberValue)
			? unknownLabel.value
			: `${formatQuantity(numberValue, { maximumFractionDigits: 1 })} m`
	}
	const positiveConstraints = constraintFlags.value
		.filter(flag => props.item[flag.key] === true)
		.map(flag => flag.label)
	if (["1", "2"].includes(String(props.item.agricultural_zoning))) {
		positiveConstraints.unshift(formatAgriculturalZoning(props.item.agricultural_zoning))
	}
	if (props.item.env_species_bool === true) {
		positiveConstraints.push(t("parcelPdfV2.threatenedSpecies"))
	}
	const checkedConstraints = [
		{ key: "env_rainette_bool", label: t("parcelPdfV2.noChorusFrog") },
		{ key: "env_floodplain_bool", label: t("parcelPdfV2.outsideFloodZone") },
		{ key: "env_wetland_bool", label: t("parcelPdfV2.noWetlands") },
		{ key: "env_soil_contaminated_bool", label: t("parcelPdfV2.noContamination") },
	]
		.filter(flag => props.item[flag.key] === false)
		.map(flag => flag.label)
	if (String(props.item.agricultural_zoning) === "0") {
		checkedConstraints.unshift(t("parcelPdfV2.outsideAgriculturalZone"))
	}
	if (props.item.env_species_bool === false) {
		checkedConstraints.push(t("parcelPdfV2.noThreatenedSpecies"))
	}
	const generatedDate = new Intl.DateTimeFormat(locale.value, {
		year: "numeric",
		month: "long",
		day: "numeric",
	}).format(new Date())
	const buildingValue =
		Number(props.item.building_value) === 0
			? null
			: row(t("parcelCard.buildingValue"), props.item.building_value, currency)
	const totalValue = row(t("parcelPdfV2.municipalAssessment"), props.item.total_property_value, currency)
	const metricRows = {
		[ParcelPdfV2Metric.MunicipalAssessment]: totalValue,
		[ParcelPdfV2Metric.Area]: row(t("parcelPdfV2.area"), props.item.official_area_m2, metricArea),
		[ParcelPdfV2Metric.Frontage]: row(t("parcelCard.frontage"), props.item.frontage_m, metricFrontage),
		[ParcelPdfV2Metric.Dwellings]: row(t("parcelPdfV2.dwellings"), props.item.number_of_dwelling_units, formatInt),
		[ParcelPdfV2Metric.LandValue]: row(t("parcelCard.landValue"), props.item.land_value, currency),
		[ParcelPdfV2Metric.SoilPotential]: row(
			t("parcelCard.soilCapability"),
			props.item.env_soil_capability,
			formatText,
			Permission.SoilCapabilityRead
		),
		[ParcelPdfV2Metric.AgriculturalZoneArea]: row(
			t("parcelCard.agriculturalZoneArea"),
			props.item.eae_agricultural_zone_area_m2,
			metricArea,
			FieldAccess.Public
		),
	}
	const parcelUrl = new URL(import.meta.env.BASE_URL, window.location.origin)
	parcelUrl.searchParams.set("parcel", String(props.item.parcel_id))

	return {
		parcelId: formatInscriptionNumber(props.item.parcel_id),
		title: `${t("parcelPdfV2.lot")} ${formatInscriptionNumber(props.item.parcel_id)}`,
		subtitle: [props.item.parcel_address, props.item.mun_name, props.item.province].filter(Boolean).join(", "),
		reportLabel: t("parcelPdfV2.reportLabel"),
		planningTitle: t("parcelPdfV2.planningTitle"),
		planningSource: t("parcelPdfV2.planningSource"),
		planningPoliciesLabel: t("parcelPdfV2.planningPolicies"),
		noPlanningPoliciesLabel: t("parcelPdfV2.noPlanningPolicies"),
		planningRows: buildRows([
			...urbanismFlags.value
				.filter(flag => props.item[flag.key] === true)
				.map(flag => row(flag.label, props.item[flag.name], formatText)),
			strategicSectorActive.value
				? row(t("parcelCard.badges.strategicSector"), strategicSectorName.value, formatText)
				: null,
		]),
		constraintsLabel: t("parcelPdfV2.identifiedConstraints"),
		constraints: positiveConstraints,
		noConstraintsLabel: t("parcelPdfV2.noConstraintIdentified"),
		checkedLabel: t("parcelPdfV2.checkedNoConstraint"),
		checkedConstraints,
		metrics: buildRows(profile.metrics.map(metric => metricRows[metric])),
		sections: [
			{ title: t("parcelCard.accordions.lot"), rows: lotRows.value },
			{
				title: t("parcelCard.accordions.developmentPotential"),
				rows: [
					...(store.currentUser?.has(Permission.DevPotentialRead)
						? [{ label: t("development-potential.title"), value: devPotentialLabel.value }]
						: []),
					...(profile.includeServicedVacantLand
						? buildRows([row(t("parcelCard.servicedVacantLand"), props.item.serviced_vacant_land, formatYesNo)])
						: []),
				],
			},
			{
				title: t("parcelCard.accordions.property"),
				rows: buildRows([
					row(t("parcelCard.cadastralArea"), props.item.official_area_m2, formatArea),
					row(t("parcelCard.frontage"), props.item.frontage_m, formatLinear),
					row(t("parcelCard.propertyUse"), props.item.property_use_label, formatText),
					row(t("parcelCard.ownerType"), props.item.owner_type, formatOwnerType),
					row(t("parcelCard.unitCount"), props.item.unit_count, formatInt),
					row(t("parcelCard.rollInscriptions"), props.item.owner_count, formatInt),
				]),
			},
			...(profile.includeBuildingDetails
				? [
						{
							title: t("parcelCard.accordions.building"),
							rows: buildRows([
								row(t("parcelCard.constructionYear"), props.item.construction_year, formatYear),
								row(t("parcelCard.storeys"), props.item.number_of_storeys, formatInt),
								row(t("parcelCard.floorArea"), props.item.floor_area_m2, metricArea),
								row(t("parcelCard.constructionType"), props.item.construction_type_label, formatText),
								row(t("parcelCard.physicalLink"), props.item.physical_link_label, formatText),
								row(t("parcelCard.dwellingUnits"), props.item.number_of_dwelling_units, formatInt),
								row(t("parcelCard.nonResidentialUnits"), props.item.number_of_non_residential_units, formatInt),
								row(t("parcelCard.rentalRooms"), props.item.number_of_rental_rooms, formatInt),
								row(t("parcelCard.nonResidentialClass"), props.item.non_residential_property_class, formatText),
								row(t("parcelCard.industrialClass"), props.item.industrial_class, formatText),
							]),
						},
					]
				: []),
			{
				title: t("parcelCard.accordions.value"),
				rows: buildRows([
					row(t("parcelCard.assessmentRollYear"), props.item.assessment_roll_year, formatYear),
					row(t("parcelCard.marketReferenceDate"), props.item.market_reference_date, formatDate),
					row(t("parcelCard.landValue"), props.item.land_value, currency),
					buildingValue,
					row(t("parcelCard.previousValue"), props.item.previous_total_property_value, currency),
					valueVariation.value
						? {
								label: t("parcelCard.variationAbsolute"),
								value: `${valueVariation.value.absolute > 0 ? "+" : ""}${currency(valueVariation.value.absolute)}`,
							}
						: null,
					valueVariation.value
						? {
								label: t("parcelCard.variationRelative"),
								value: `${valueVariation.value.relative > 0 ? "+" : ""}${formatPercent(valueVariation.value.relative)}`,
							}
						: null,
				]),
				highlight: totalValue ?? undefined,
			},
			{
				title: t("parcelCard.accordions.environment"),
				rows: buildRows([
					row(
						t("parcelCard.soilCapability"),
						props.item.env_soil_capability,
						formatText,
						Permission.SoilCapabilityRead
					),
					row(
						t("parcelCard.agriculturalZoneArea"),
						props.item.eae_agricultural_zone_area_m2,
						formatArea,
						FieldAccess.Public
					),
					row(
						t("parcelCard.forestZoneArea"),
						props.item.svfe_agricultural_zone_area_m2,
						formatArea,
						FieldAccess.Public
					),
					row(t("parcelCard.maxTaxableArea"), props.item.eae_max_taxable_area_m2, formatArea, FieldAccess.Public),
					...(props.item.env_wetland_bool === true
						? [
								row(t("parcelCard.wetlandShare"), props.item.env_wetland_per, formatPercent, Permission.WetlandsRead),
								row(t("parcelCard.wetlandArea"), props.item.env_wetland_area, formatArea, Permission.WetlandsRead),
							]
						: []),
					row(
						t("parcelCard.protectedAnimals"),
						props.item.env_protected_animals,
						formatText,
						Permission.EndangeredSpeciesRead
					),
					row(
						t("parcelCard.protectedPlants"),
						props.item.env_protected_plants,
						formatText,
						Permission.EndangeredSpeciesRead
					),
					...(props.item.env_soil_contaminated_bool === true
						? [
								row(
									t("parcelCard.soilRehabStatus"),
									props.item.env_soil_contaminated_rehab,
									formatText,
									FieldAccess.Public
								),
							]
						: []),
				]),
			},
		],
		maps: [],
		mapAttribution: t("parcelPdfV2.mapAttribution"),
		parcelUrl: parcelUrl.toString(),
		qrTitle: t("parcelPdfV2.qrTitle"),
		qrDescription: t("parcelPdfV2.qrDescription"),
		disclaimer: t("parcelPdfV2.disclaimer"),
		footerBrand: "Landerz · landerz.ca",
		footerSource: t("parcelPdfV2.footerSource", { date: generatedDate }),
		continuedLabel: t("parcelPdfV2.continued"),
		pageLabel: (page, pageCount) => t("parcelPdfV2.pageLabel", { page, pageCount }),
	}
}

async function updateDevPotential(potential: DevelopmentPotential): Promise<void> {
	const parcelId = props.item.parcel_id
	if (!parcelId) return
	const id = parcelId.toString()
	const previousPotential = prospectStore.prospects.find(p => p.prospectID === id)?.potential

	prospectStore.updateByParcelId(id, { potential })

	const success = await setProspectPotential(store.userApiToken, parcelId, potential)

	if (!success) {
		prospectStore.updateByParcelId(id, { potential: previousPotential ?? DevelopmentPotential.Unspecified })
		console.error(`Failed to update development potential for parcel: ${id}`)
		toastStore.error("development-potential-messages.error")
		return
	}

	props.mapRef?.refreshProspectsLayer?.()
	toastStore.success("development-potential-messages.success")
}

// Actions: copy lot number, maps link, export, create opportunity, create transaction

const coords = ref<{ latitude?: string; longitude?: string }>({})

const mapsLinks = computed<EntityLink[]>(() => {
	if (!coords.value.latitude || !coords.value.longitude) return []
	return [
		{
			id: "maps",
			label: t("callToActions.openInMaps"),
			icon: "map-pin",
			href: `https://www.google.com/maps/search/?api=1&query=${coords.value.latitude},${coords.value.longitude}`,
			target: "_blank",
		},
	]
})

async function loadParcelCoordinates(): Promise<void> {
	const parcelId = props.item.parcel_id?.toString()
	if (!parcelId || !/^\d+$/.test(parcelId)) return
	try {
		const parcel = await getParcel(parcelId)
		coords.value = parcel
			? { latitude: parcel.location.coordinates.latitude, longitude: parcel.location.coordinates.longitude }
			: {}
	} catch (error) {
		console.error(`Error loading parcel ${parcelId}`, error)
		coords.value = {}
	}
}

watchEffect(() => {
	loadParcelCoordinates()
})

const isExporting = ref(false)

async function downloadParcel(format: ParcelExportFormat): Promise<void> {
	const parcelId = String(props.item.parcel_id)
	isExporting.value = true
	try {
		const [data, filename] = await store.getParcelExport(parcelId, format)
		download.push(data, filename)
	} catch (error) {
		console.error(`${format.toUpperCase()} download failed:`, error)
	} finally {
		isExporting.value = false
	}
}

async function downloadParcelReport(): Promise<void> {
	const parcelId = String(props.item.parcel_id)
	isExporting.value = true
	try {
		const [data, filename] = await store.getParcelReport(parcelId, parcelContent.value)
		download.push(data, filename)
		extendedAccessStore.recordParcelReportDownload()
	} catch (error) {
		console.error("PDF download failed:", error)
	} finally {
		isExporting.value = false
	}
}

function exportParcelReport(): void {
	if (config.ui.has(Feature.ParcelPdfV2)) exportParcelPdfV2()
	else downloadParcelReport()
}

function onExportMenuSelect(details: { value: string }): void {
	if (details.value === "pdf") {
		exportParcelReport()
		return
	}
	downloadParcel(details.value as ParcelExportFormat)
}

const parcelZones = ref<Zone[]>([])

const zoningDetails = computed(() => zoneDetails(parcelZones.value, t))
const isZoningLoading = ref(false)

async function loadParcelZoning(parcelId: string): Promise<Zone[]> {
	try {
		return await store.getParcelZoning(parcelId)
	} catch (error) {
		console.error("Parcel zoning unavailable:", error)
		return []
	}
}

watch(
	() => [String(props.item.parcel_id), canReadZoning.value] as const,
	([parcelId, canReadZoning]) => {
		parcelZones.value = []
		isZoningLoading.value = canReadZoning
		if (!canReadZoning) return
		loadParcelZoning(parcelId).then(zones => {
			if (String(props.item.parcel_id) !== parcelId) return
			parcelZones.value = zones
			isZoningLoading.value = false
		})
	},
	{ immediate: true }
)

async function exportParcelPdfV2(): Promise<void> {
	if (isExporting.value) return
	const parcelId = String(props.item.parcel_id)
	isExporting.value = true
	try {
		const content = buildParcelReportV2Content()
		if (config.ui.has(Feature.ParcelPdfV2Maps)) {
			try {
				const parcel = coords.value.latitude && coords.value.longitude ? null : await getParcel(Number(parcelId))
				const latitude = Number(coords.value.latitude ?? parcel?.location.coordinates.latitude)
				const longitude = Number(coords.value.longitude ?? parcel?.location.coordinates.longitude)
				if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
					throw new Error("Parcel coordinates are unavailable")
				}
				const views = [{ view: ParcelMapView.Location, title: t("parcelPdfV2.mapTitle") }]
				if (store.currentUser?.has(Permission.TransportationRead)) {
					views.push({ view: ParcelMapView.Transportation, title: t("parcelPdfV2.transportMapTitle") })
				}
				if (store.currentUser?.has(Permission.WetlandsRead)) {
					views.push({ view: ParcelMapView.Wetlands, title: t("parcelPdfV2.wetlandsMapTitle") })
				}
				if (store.currentUser?.has(Permission.FloodableZonesRead)) {
					views.push({ view: ParcelMapView.FloodableZones, title: t("parcelPdfV2.floodableZonesMapTitle") })
				}
				for (const { view, title } of views) {
					try {
						const image = await captureParcelMap({
							parcelId,
							latitude,
							longitude,
							apiToken: store.userApiToken,
							view,
						})
						content.maps.push({ title, image })
					} catch (error) {
						console.error(`${view} map capture failed:`, error)
						toastStore.error("parcelPdfV2.mapCaptureFailed")
					}
				}
			} catch (error) {
				console.error("Parcel map coordinates unavailable:", error)
				toastStore.error("parcelPdfV2.mapCaptureFailed")
			}
		}
		createParcelPdfV2(content, landerzLogo).save(`parcel-${parcelId}.pdf`)
		extendedAccessStore.recordParcelReportDownload()
	} finally {
		isExporting.value = false
	}
}

const isQuickOpportunityLoading = ref(false)

async function handleQuickOpportunity(): Promise<void> {
	try {
		isQuickOpportunityLoading.value = true
		const projectId = currentProject.value?.id
		if (!projectId) {
			toastStore.error("opportunity-creation.messages.error")
			return
		}

		const opportunityId = await createOpportunity(store.userApiToken, {
			parcelIDs: [props.item.parcel_id],
			totalAreaSqft: Number(props.item.area_square_feet) || 0,
			marketingType: "internal",
			projectId,
			...(currentProject.value?.type !== ProjectType.Workspace
				? { ccaContractHubspotId: currentProject.value?.ccaContractHubspotId }
				: {}),
		})

		if (opportunityId == null) {
			toastStore.error("opportunity-creation.messages.error")
			return
		}

		props.mapRef?.registerPendingTargetSync?.(opportunityId)
		opportunityStore.invalidateProjectOpportunities(projectId, { selectOpportunityId: opportunityId })
		toastStore.success("opportunity-creation.messages.success")
	} catch (error) {
		console.error("Error creating opportunity:", error)
		toastStore.error("opportunity-creation.messages.error")
	} finally {
		isQuickOpportunityLoading.value = false
	}
}

const inscriptionNumber = ref("")
const createTransactionEnabled = ref(false)
const showTransactionForm = ref(false)
const transactionInputEl = ref<HTMLInputElement | null>(null)
const transactionSectionEl = ref<HTMLElement | null>(null)

async function openTransactionForm(): Promise<void> {
	showTransactionForm.value = true
	await nextTick()
	transactionInputEl.value?.focus()
}

function handleTransactionButtonClick(event: MouseEvent): void {
	if (!showTransactionForm.value) {
		openTransactionForm()
		return
	}
	;(event.currentTarget as HTMLElement)?.closest("form")?.requestSubmit()
}

onClickOutside(transactionSectionEl, () => {
	showTransactionForm.value = false
})

async function handleCreateTransaction(): Promise<void> {
	try {
		const transaction = await store.createTransaction(inscriptionNumber.value, [props.item.parcel_id])
		store.addSelectedTransaction(transaction)
	} catch (error) {
		console.warn("Transaction creation failed:", error)
		toastStore.error("notifications.defaultError")
	}
}
</script>

<style scoped>
.parcel-card {
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: var(--neutral-0);

	.top-section {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		padding: var(--spacing-md) var(--spacing-lg) 0;
	}

	.scroll-body {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		padding: var(--spacing-md) var(--spacing-lg);
	}

	.header {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		.header-top {
			display: flex;
			justify-content: space-between;
			align-items: end;
			gap: var(--spacing-sm);
		}

		.title-group {
			display: flex;
			align-items: center;
			gap: var(--spacing-xxs);

			.maps-link :deep(.icon-button) {
				width: var(--size-icon-lg);
				height: var(--size-icon-lg);
				border: none;

				&:hover {
					background-color: transparent;
					color: var(--primary-green);
				}
			}
		}

		.header-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			flex-shrink: 0;
		}

		.title {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		.sub-title {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-regular);
			line-height: var(--line-height-normal);
		}

		.badge-groups {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-md);
			margin-top: var(--spacing-xs);
		}

		.badge-group {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);

			.group-label {
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
				text-transform: uppercase;
				letter-spacing: 0.02em;
			}
		}

		.badge-row {
			display: flex;
			flex-wrap: wrap;
			gap: var(--spacing-xs);
		}

		.badge {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);

			&.badge--urbanism {
				background-color: var(--primary-green);
				color: var(--neutral-0);
			}

			&.badge--constraint {
				background-color: color-mix(in srgb, var(--accent-warning) 12%, transparent);
				color: color-mix(in srgb, var(--accent-warning) 70%, black);
			}

			&.badge--zone {
				border: var(--border-width-thin) solid var(--neutral-200);
				color: var(--neutral-700);
				font-weight: var(--font-weight-medium);
			}
		}
	}

	.icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		padding: 0;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		background: var(--neutral-0);
		color: var(--neutral-600);
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			color: var(--primary-green);
			border-color: var(--primary-green);
		}

		&.collapse-btn {
			width: auto;
			height: auto;
			padding: var(--spacing-sm);
			border: none;
			border-radius: var(--border-radius-sm);
			background: none;

			&:hover {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
	}

	.view-switch {
		padding: var(--spacing-xxs);
		background-color: var(--neutral-50);
		border: var(--border-width-thin) solid var(--neutral-100);

		& :deep(.option) {
			gap: var(--spacing-xs);
			padding: var(--spacing-xs) var(--spacing-sm);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			line-height: 1;
		}

		& :deep(.app-icon) {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		flex-shrink: 0;
		background-color: var(--neutral-400);
	}

	.stat-tiles {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--spacing-sm);

		.tile {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			padding: var(--spacing-sm) var(--spacing-md);
			border-radius: var(--border-radius-lg);
			background: var(--neutral-50);

			.label {
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
				text-transform: uppercase;
				letter-spacing: 0.02em;
			}

			.value-group {
				display: flex;
				align-items: center;
				gap: var(--spacing-xxs);
			}

			.value {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-medium);
				font-weight: var(--font-weight-semibold);
				line-height: var(--line-height-tight);
				white-space: nowrap;
			}
		}
	}

	.parcel-actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);

		.action-slot {
			flex: 1 1 0%;
			min-width: 90px;
			height: 28px;

			.loader {
				border-color: var(--neutral-700);
				border-bottom-color: transparent;
			}

			.export-icon {
				flex-shrink: 0;
				margin-right: var(--spacing-xxs);
			}

			.menu-trigger-chevron {
				margin-left: var(--spacing-xxs);
				flex-shrink: 0;
			}
		}
	}

	.export-menu-item {
		display: flex;
		flex-direction: column;
		gap: 2px;

		.format {
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
		}

		.description {
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			line-height: var(--line-height-normal);
		}
	}

	.loader {
		width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		border: var(--border-width-base) solid var(--neutral-900);
		border-bottom-color: transparent;
		border-radius: var(--border-radius-full);
		display: inline-block;
		animation: rotation 1s linear infinite;
	}

	@keyframes rotation {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	@keyframes pulse {
		50% {
			opacity: 0.5;
		}
	}

	.actions-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.transaction-section {
		container-type: inline-size;
		container-name: transaction-section;

		.transaction-form {
			display: grid;
			grid-template-columns: 0fr auto;
			column-gap: 0;
			align-items: stretch;
			height: 28px;
			transition:
				grid-template-columns 0.45s ease-in-out,
				column-gap 0.45s ease-in-out;

			&.expanded {
				grid-template-columns: 1fr auto;
				column-gap: var(--spacing-xs);
			}
		}

		.input-wrapper {
			min-width: 0;
			overflow: hidden;
		}

		.create-transaction-btn {
			min-width: max-content;
			height: 100%;

			.label-short {
				display: none;
			}
		}

		@container transaction-section (max-width: 280px) {
			.transaction-form.expanded .create-transaction-btn {
				.label-full {
					display: none;
				}

				.label-short {
					display: inline;
				}
			}
		}

		.input-transaction {
			width: 100%;
			height: 100%;
			padding: 0 var(--spacing-sm) 0 var(--space-10);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);
			text-align: center;
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			transition: border-color 0.2s ease;

			&:hover:not(:disabled) {
				border-color: var(--neutral-600);
			}

			&:focus,
			&:focus-visible {
				outline: none;
				border-color: var(--neutral-600);
				box-shadow: var(--shadow-sm);
			}

			&:invalid:not(:empty) {
				border-color: var(--accent-warning);
			}
		}
	}

	.dev-potential-section {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
		padding: 6px 0;
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		&:last-child {
			border-bottom: none;
		}

		label {
			flex: 1 1 auto;
			min-width: 0;
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
		}

		:deep(.ark-listbox-dropdown) {
			flex: 0 0 auto;

			.trigger {
				height: var(--space-24, 24px);
				padding: 0 var(--spacing-xs) 0 var(--space-10);
				border-color: var(--neutral-300);
			}

			.display-text {
				font-size: var(--font-size-small);
			}
		}
	}

	.actions-box {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-50);

		> .label {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.access-note {
			margin: 0;
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
		}
	}

	.cta-box {
		display: flex;
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-50);

		.cta-button {
			width: 100%;
			gap: var(--spacing-xs);
		}
	}

	.accordions {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		:deep(.accordion-content) {
			padding: var(--spacing-sm);
		}

		.zoning {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-md);

			.zone {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-sm);
			}

			.skeleton-rows {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-sm);

				.skeleton-row {
					display: flex;
					justify-content: space-between;
					padding: 6px 0;
				}

				.skeleton {
					height: var(--spacing-sm);
					border-radius: var(--border-radius-sm);
					background: var(--neutral-200);
					animation: pulse 1.5s ease-in-out infinite;

					&.label {
						width: 35%;
					}

					&.value {
						width: 25%;
					}
				}
			}

			.norms {
				padding-top: var(--spacing-xs);
				border-top: var(--border-width-thin) solid var(--neutral-200);
			}

			.blocks {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-sm);
				padding-top: var(--spacing-xs);
			}

			.block {
				padding: var(--spacing-xs) var(--spacing-sm);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-lg);
				background: var(--neutral-50);

				&.title {
					color: var(--neutral-900);
					font-family: var(--font-family-primary);
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-medium);
				}
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			justify-content: space-between;

			.detail {
				display: flex;
				flex-wrap: wrap;
				gap: var(--spacing-xxs) var(--spacing-sm);
				padding: 6px 0;
				justify-content: space-between;
				align-items: baseline;
				border-bottom: var(--border-width-thin) solid var(--neutral-200);

				&:last-child {
					border-bottom: none;
				}

				.label {
					flex: 1 1 var(--min-width-field);
					color: var(--neutral-600);
					font-family: var(--font-family-primary);
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-medium);
					line-height: var(--line-height-normal);
				}

				.value {
					color: var(--neutral-900);
					font-family: var(--font-family-primary);
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-regular);
					line-height: var(--line-height-normal);
				}
			}
		}
	}
}
</style>
